import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, ChevronLeft, ChevronRight, Upload, Loader2, Trash2, Eye, Heart } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import ReportButton from "@/components/ReportButton";

const STORY_DURATION_MS = 15000; // 15 seconds for images
const STORY_VIDEO_DURATION_MS = 15000; // 15 seconds cap for videos (do not loop; if shorter, advance on video-end)

function getVisitorId() {
  let v = localStorage.getItem("kh_visitor_id");
  if (!v) {
    v = (crypto.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("kh_visitor_id", v);
  }
  return v;
}

/**
 * Stories: horizontal row of circular avatars at top of Home.
 * - Shop owners see a "+" avatar to publish a story.
 * - Anyone can tap an avatar to open the full-screen viewer.
 */
export default function Stories() {
  const [groups, setGroups] = useState([]);
  const [viewer, setViewer] = useState(null); // { groupIdx, storyIdx }
  const [addOpen, setAddOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();

  const canPost = user?.role === "shop_owner" || user?.role === "admin" || !!user?.can_write_news;

  const load = () => {
    api.get("/stories").then(r => setGroups(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!canPost && groups.length === 0) return null;

  const openViewer = (groupIdx) => setViewer({ groupIdx, storyIdx: 0 });
  const closeViewer = () => setViewer(null);

  const nextStory = () => {
    setViewer(v => {
      if (!v) return v;
      const g = groups[v.groupIdx];
      if (v.storyIdx + 1 < g.stories.length) return { ...v, storyIdx: v.storyIdx + 1 };
      if (v.groupIdx + 1 < groups.length) return { groupIdx: v.groupIdx + 1, storyIdx: 0 };
      return null; // done
    });
  };
  const prevStory = () => {
    setViewer(v => {
      if (!v) return v;
      if (v.storyIdx > 0) return { ...v, storyIdx: v.storyIdx - 1 };
      if (v.groupIdx > 0) {
        const g = groups[v.groupIdx - 1];
        return { groupIdx: v.groupIdx - 1, storyIdx: g.stories.length - 1 };
      }
      return v;
    });
  };

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4" data-testid="stories-section">
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x kh-scroll-x">
        {canPost && (
          <button
            onClick={(e) => { e.stopPropagation(); setAddOpen(true); }}
            className="shrink-0 flex flex-col items-center gap-1.5 w-[76px]"
            data-testid="story-add-btn"
          >
            <div className="relative size-[68px] rounded-full bg-gradient-to-br from-primary to-emerald-600 p-[3px] shadow-md hover:scale-105 transition">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <Plus className="size-7 text-primary" strokeWidth={2.5} />
              </div>
            </div>
            <span className="text-[11px] font-bold text-primary line-clamp-1">{t("story.add")}</span>
          </button>
        )}

        {groups.map((g, i) => {
          const isAdmin = g.is_admin_story;
          const isJournalist = g.is_journalist && !isAdmin;
          const ringClass = isAdmin
            ? "bg-gradient-to-tr from-amber-500 via-yellow-400 to-orange-500"
            : isJournalist
            ? "bg-gradient-to-tr from-blue-500 via-cyan-400 to-sky-300"
            : "bg-gradient-to-tr from-amber-400 via-rose-500 to-fuchsia-600";
          return (
            <button
              key={g.shop_id || `journalist-${i}`}
              onClick={() => openViewer(i)}
              className="shrink-0 flex flex-col items-center gap-1.5 w-[76px]"
              data-testid={`story-avatar-${g.shop_id || (isAdmin ? 'admin' : 'journalist')}-${i}`}
            >
              <div className={`relative size-[68px] rounded-full p-[3px] ${ringClass} hover:scale-105 transition shadow-md`}>
                <div className="w-full h-full rounded-full bg-white p-[2px]">
                  {g.shop_logo ? (
                    <img
                      src={g.shop_logo}
                      alt={g.shop_name}
                      className="w-full h-full rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className={`w-full h-full rounded-full ${isAdmin ? "bg-gradient-to-br from-amber-500 to-orange-700" : isJournalist ? "bg-gradient-to-br from-blue-500 to-sky-700" : "bg-gradient-to-br from-primary to-emerald-700"} text-white flex items-center justify-center font-black`}>
                      {isAdmin ? "👑" : isJournalist ? "📰" : (g.shop_name || "?").charAt(0)}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="absolute -bottom-1 -end-1 bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                    الإدارة
                  </div>
                )}
                {isJournalist && (
                  <div className="absolute -bottom-1 -end-1 bg-blue-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                    {t("story.reporter")}
                  </div>
                )}
              </div>
              <span className="text-[11px] font-semibold text-foreground line-clamp-1">{g.shop_name}</span>
            </button>
          );
        })}
      </div>

      {viewer && (
        <StoryViewer
          groups={groups}
          groupIdx={viewer.groupIdx}
          storyIdx={viewer.storyIdx}
          user={user}
          onClose={closeViewer}
          onNext={nextStory}
          onPrev={prevStory}
          onDelete={async (storyId) => {
            try {
              await api.delete(`/stories/${storyId}`);
              toast.success("تم حذف الستوري");
              closeViewer();
              load();
            } catch (e) {
              toast.error("فشل الحذف");
            }
          }}
        />
      )}

      {addOpen && (
        <AddStoryDialog
          onClose={() => setAddOpen(false)}
          onCreated={() => { load(); setAddOpen(false); }}
        />
      )}
    </section>
  );
}

function StoryViewer({ groups, groupIdx, storyIdx, user, onClose, onNext, onPrev, onDelete }) {
  const group = groups[groupIdx];
  const story = group?.stories[storyIdx];
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const nav = useNavigate();
  const startRef = useRef(Date.now());
  const rafRef = useRef();
  const elapsedRef = useRef(0);
  const dragStartRef = useRef(null);

  // ⚡ While the viewer is open, hide the app's chrome (header + bottom nav)
  // so the story is truly fullscreen like Instagram. We toggle a class on
  // <body> and rely on CSS to hide the fixed/sticky navigation bars.
  useEffect(() => {
    document.body.classList.add("story-viewer-open");
    return () => document.body.classList.remove("story-viewer-open");
  }, []);
  const holdTimerRef = useRef(null);
  const movedRef = useRef(false);
  const { t } = useLang();

  useEffect(() => {
    startRef.current = Date.now();
    elapsedRef.current = 0;
    setProgress(0);
    // Record a unique view for this story (backend dedupes by visitor_id)
    if (story?.id) {
      api.post(`/stories/${story.id}/view`, { visitor_id: getVisitorId() }).catch(() => {});
    }
  }, [groupIdx, storyIdx, story?.id]);

  useEffect(() => {
    if (paused) {
      cancelAnimationFrame(rafRef.current);
      elapsedRef.current += Date.now() - startRef.current;
      return;
    }
    startRef.current = Date.now();
    const durationMs = story?.media_type === "video" ? STORY_VIDEO_DURATION_MS : STORY_DURATION_MS;
    const tick = () => {
      const dt = Date.now() - startRef.current + elapsedRef.current;
      const pct = Math.min(1, dt / durationMs);
      setProgress(pct);
      if (pct >= 1) {
        onNext();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [groupIdx, storyIdx, paused, onNext, story?.media_type]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onPrev();
      else if (e.key === "ArrowLeft") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNext, onPrev]);

  // Instagram-style vertical drag-to-dismiss
  const onPointerDown = (e) => {
    // Ignore drags starting on interactive controls
    if (e.target.closest("[data-story-control]")) return;
    dragStartRef.current = { y: e.clientY, x: e.clientX, t: Date.now() };
    movedRef.current = false;
    // Hold-to-pause after short delay (only if user hasn't moved)
    holdTimerRef.current = setTimeout(() => {
      if (!movedRef.current) setPaused(true);
    }, 180);
  };

  const onPointerMove = (e) => {
    if (!dragStartRef.current) return;
    const dy = e.clientY - dragStartRef.current.y;
    const dx = e.clientX - dragStartRef.current.x;
    if (!movedRef.current && (Math.abs(dy) > 6 || Math.abs(dx) > 6)) {
      movedRef.current = true;
      clearTimeout(holdTimerRef.current);
    }
    // Track only vertical drags (Instagram behavior)
    if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
      setDragging(true);
      setPaused(true);
      setDragY(dy);
    }
  };

  const onPointerUp = () => {
    clearTimeout(holdTimerRef.current);
    setPaused(false);
    // Instagram uses ~80-100px threshold — easier swipe-down to close
    if (dragY > 80) {
      onClose();
    } else {
      setDragY(0);
    }
    setDragging(false);
    dragStartRef.current = null;
    movedRef.current = false;
  };

  if (!story) return null;

  // Compute dim + scale based on drag distance (Instagram feel)
  const dragProgress = Math.min(1, dragY / 300);
  const bgOpacity = 1 - dragProgress * 0.6;
  const scale = 1 - dragProgress * 0.06;
  const radius = dragY > 0 ? Math.min(28, dragY * 0.14) : 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center touch-none select-none"
      style={{
        backgroundColor: `rgba(0,0,0,${bgOpacity.toFixed(3)})`,
        // Use dynamic viewport units so notches/status bars are covered
        height: "100dvh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="story-viewer"
      data-no-pull-refresh
    >
      {/* Instagram-style fullscreen story that moves with the drag */}
      <div
        className="relative w-full h-full bg-black overflow-hidden shadow-2xl"
        style={{
          transform: `translateY(${dragY}px) scale(${scale})`,
          borderRadius: `${radius}px`,
          transition: dragging ? "none" : "transform 260ms cubic-bezier(0.22,1,0.36,1), border-radius 260ms",
          willChange: "transform, border-radius",
        }}
      >
        {/* Media - full-screen contain preserves the original aspect ratio & resolution */}
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {story.media_type === "video" ? (
            <video
              src={story.media_url || story.image}
              className="w-full h-full object-contain"
              autoPlay
              playsInline
              controls={false}
              muted={false}
              onEnded={onNext}
              data-testid="story-video"
            />
          ) : (
            <img
              src={story.media_url || story.image}
              alt=""
              className="w-full h-full object-contain"
              draggable={false}
            />
          )}
          {/* Subtle top gradient for legibility of header */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
          {/* Subtle bottom gradient for caption */}
          {story.caption && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent" />
          )}
        </div>

        {/* Progress bars */}
        <div className="absolute top-0 inset-x-0 pt-2.5 px-2 flex gap-1 z-20">
          {group.stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-none"
                style={{ width: `${i < storyIdx ? 100 : i === storyIdx ? progress * 100 : 0}%` }}
              />
            </div>
          ))}
        </div>

        {/* Header row: avatar + name + close (Instagram style) */}
        <div className="absolute top-5 inset-x-0 px-3 z-20 flex items-center justify-between gap-2" data-story-control>
          <button
            onClick={() => { if (group.shop_id) { onClose(); nav(`/shop/${group.shop_id}`); } }}
            disabled={!group.shop_id}
            className="flex items-center gap-2.5 text-white active:scale-95 transition disabled:cursor-default min-w-0"
            data-testid="story-goto-shop"
            data-story-control
          >
            <div className="size-8 rounded-full bg-white/20 overflow-hidden ring-2 ring-white/90 shrink-0">
              {group.shop_logo ? (
                <img src={group.shop_logo} alt={group.shop_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-black text-sm">
                  {group.is_admin_story ? "👑" : group.is_journalist ? "📰" : (group.shop_name || "?").charAt(0)}
                </div>
              )}
            </div>
            <div className="text-start min-w-0">
              <div className="text-[13px] font-bold leading-tight flex items-center gap-1 truncate drop-shadow">
                <span className="truncate">{group.shop_name}</span>
                {group.is_admin_story && (
                  <span className="bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0">الإدارة</span>
                )}
                {group.is_journalist && !group.is_admin_story && (
                  <span className="bg-blue-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0">{t("story.reporter")}</span>
                )}
              </div>
              <div className="text-[10px] text-white/80 drop-shadow truncate">
                {group.shop_id ? t("story.visit_shop") : group.is_admin_story ? "من إدارة هود" : t("story.news_of_kh")}
              </div>
            </div>
          </button>
          <div className="flex items-center gap-1.5 shrink-0" data-story-control>
            {user?.id && user?.role !== "admin" && group.owner_id && group.owner_id !== user.id && (
              <div data-story-control className="[&_button]:!bg-black/50 [&_button]:!text-white [&_button]:!size-9 [&_button]:!rounded-full [&_button:hover]:!bg-black/70 [&_button]:!p-0 [&_button_span]:!hidden">
                <ReportButton
                  contentType="story"
                  contentId={story.id}
                  ownerUserId={group.owner_id}
                  size="icon"
                  variant="ghost"
                  label=""
                />
              </div>
            )}
            {user?.role === "admin" && (
              <button
                onClick={() => {
                  if (window.confirm("حذف هذا الستوري نهائياً؟ (خاصية إدارة)")) onDelete(story.id);
                }}
                className="text-white size-9 rounded-full bg-rose-600/80 hover:bg-rose-700 active:scale-90 flex items-center justify-center transition"
                data-testid="story-admin-delete"
                title="حذف (إدارة)"
                data-story-control
              >
                <Trash2 className="size-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white size-9 rounded-full bg-white/15 hover:bg-white/25 active:scale-90 flex items-center justify-center transition"
              data-testid="story-close"
              data-story-control
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* Views + Likes counter — visible only to admin, story author, or shop owner */}
        {(() => {
          const isAdmin = user?.role === "admin";
          const isAuthor = group.is_journalist && group.author_id === user?.id;
          const isShopOwner = !group.is_journalist && group.owner_id && group.owner_id === user?.id;
          if (!(isAdmin || isAuthor || isShopOwner)) return null;
          return (
            <StoryStatsBadge storyId={story.id} initialViews={story.views || 0} initialLikes={story.likes || 0} />
          );
        })()}

        {/* Customer Like button (heart) — visible only to non-owners */}
        {(() => {
          const isAdmin = user?.role === "admin";
          const isAuthor = group.is_journalist && group.author_id === user?.id;
          const isShopOwner = !group.is_journalist && group.owner_id && group.owner_id === user?.id;
          if (isAdmin || isAuthor || isShopOwner) return null;
          return (
            <StoryLikeButton storyId={story.id} userId={user?.id} initialLikes={story.likes || 0} initialLikedBy={story.liked_by || []} />
          );
        })()}

        {/* Caption at bottom (Instagram-like) */}
        {story.caption && (
          <div className="absolute bottom-6 inset-x-0 px-5 z-10 text-center pointer-events-none">
            <div className="inline-block text-white text-sm sm:text-base font-semibold leading-relaxed drop-shadow-lg [text-shadow:_0_2px_4px_rgba(0,0,0,0.9)]">
              {story.caption}
            </div>
          </div>
        )}

        {/* Tap zones - only trigger on tap (not on drag) */}
        <button
          onClick={(e) => { if (!movedRef.current) onPrev(); }}
          className="absolute inset-y-0 start-0 w-1/3 z-10"
          aria-label="previous"
          data-story-control
        >
          <ChevronRight className="size-8 text-white/0 mx-3" />
        </button>
        <button
          onClick={(e) => { if (!movedRef.current) onNext(); }}
          className="absolute inset-y-0 end-0 w-1/3 z-10"
          aria-label="next"
          data-story-control
        >
          <ChevronLeft className="size-8 text-white/0 mx-3" />
        </button>
      </div>
    </div>
  );
}

function StoryLikeButton({ storyId, userId, initialLikes = 0, initialLikedBy = [] }) {
  const [liked, setLiked] = useState(userId ? initialLikedBy.includes(userId) : false);
  const [likes, setLikes] = useState(initialLikes);
  const [busy, setBusy] = useState(false);
  const [burst, setBurst] = useState(false);

  const toggle = async (e) => {
    // Stop the parent story-viewer pointer/click handlers from swallowing the tap.
    if (e && e.stopPropagation) e.stopPropagation();
    if (busy) return;
    if (!userId) { toast.info("سجّل دخول أولاً لتضغط إعجاب"); return; }
    setBusy(true);
    // Optimistic
    const prev = liked;
    setLiked(!prev);
    setLikes(l => Math.max(0, l + (prev ? -1 : 1)));
    if (!prev) { setBurst(true); setTimeout(() => setBurst(false), 600); }
    try {
      const { data } = await api.post(`/stories/${storyId}/like`);
      setLiked(!!data.liked);
      setLikes(data.likes);
    } catch (err) {
      // Revert on failure
      setLiked(prev);
      setLikes(l => Math.max(0, l + (prev ? 1 : -1)));
      toast.error("تعذّر إرسال الإعجاب، حاول مجدداً");
    }
    setBusy(false);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      className="absolute bottom-4 end-4 z-30 flex items-center gap-1.5 bg-black/50 backdrop-blur text-white px-4 py-3 rounded-full active:scale-90 transition"
      data-testid="story-like-btn"
      data-story-control
      aria-label={liked ? "إلغاء الإعجاب" : "إعجاب"}
    >
      <Heart
        className={`size-5 transition-transform ${liked ? "fill-rose-500 text-rose-500" : "text-white"} ${burst ? "scale-150" : ""}`}
      />
      {likes > 0 && <span className="text-xs font-black">{likes}</span>}
    </button>
  );
}

function StoryStatsBadge({ storyId, initialViews, initialLikes }) {
  const [showList, setShowList] = useState(false);
  const [stats, setStats] = useState({ views: initialViews, unique_viewers: 0, likes: initialLikes, viewers: [], likers: [] });

  // 🔄 Auto-refresh badge stats every 5s so the owner sees new views/likes
  // live while looking at their own story. Also fetches once immediately.
  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const { data } = await api.get(`/stories/${storyId}/stats`);
        if (alive) setStats(data);
      } catch { /* silent — owner will just see the initial numbers */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [storyId]);

  const openList = async () => {
    try {
      const { data } = await api.get(`/stories/${storyId}/stats`);
      setStats(data);
    } catch { /* silent */ }
    setShowList(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openList}
        className="absolute bottom-4 start-4 z-20 flex items-center gap-2 bg-black/60 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-black/80 active:scale-95 transition"
        data-testid="story-stats-badge"
        data-story-control
      >
        <Eye className="size-3.5" />
        <span>{stats.views}</span>
        <span className="text-white/40">|</span>
        <Heart className="size-3.5 text-rose-400 fill-rose-400" />
        <span>{stats.likes}</span>
        <span className="text-[10px] text-white/60 ms-1">▲ اضغط للتفاصيل</span>
      </button>

      {showList && (
        <div className="absolute inset-0 z-30 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowList(false)} data-story-control>
          <div className="w-full max-w-md bg-background text-foreground rounded-t-3xl sm:rounded-3xl p-5 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="story-viewers-list">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-lg">من شاهد قصتك</h3>
              <button onClick={() => setShowList(false)} className="size-8 rounded-full hover:bg-muted flex items-center justify-center">
                <X className="size-4" />
              </button>
            </div>
            <div className="flex items-center gap-4 mb-4 py-3 border-y">
              <div className="flex items-center gap-1.5"><Eye className="size-4" /><span className="font-bold">{stats.views}</span><span className="text-xs text-muted-foreground">مشاهدة</span></div>
              <div className="flex items-center gap-1.5"><Heart className="size-4 text-rose-500 fill-rose-500" /><span className="font-bold">{stats.likes}</span><span className="text-xs text-muted-foreground">إعجاب</span></div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">({stats.unique_viewers} فريد)</div>
            </div>
            {stats.viewers.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">لا مشاهدين بعد</div>
            ) : (
              <div className="space-y-2">
                {stats.viewers.slice().reverse().map((v, i) => {
                  const didLike = (stats.likers || []).some(l => l.id === v.id);
                  return (
                    <div key={v.id + i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted">
                      <div className="size-9 rounded-full bg-primary/15 flex items-center justify-center overflow-hidden shrink-0">
                        {v.avatar ? <img src={v.avatar} alt="" className="w-full h-full object-cover" /> : <span className="font-black">{(v.name || "?").charAt(0)}</span>}
                      </div>
                      <div className="text-sm font-bold flex-1 truncate">{v.name}</div>
                      {didLike && <Heart className="size-4 text-rose-500 fill-rose-500 shrink-0" aria-label="أعجب" />}
                      {v.anonymous && <span className="text-[10px] text-muted-foreground">مجهول</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function AddStoryDialog({ onClose, onCreated }) {  const [myShops, setMyShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [media, setMedia] = useState("");
  const [mediaType, setMediaType] = useState("image"); // "image" | "video"
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("shop"); // "shop" | "journalist"
  const { user } = useAuth();

  const canJournalist = user?.role === "admin" || !!user?.can_write_news;
  const isAdmin = user?.role === "admin";
  const journalistLabel = isAdmin ? "👑 ستوري الإدارة (مثبّت أولاً)" : "📰 ستوري مراسل (مثبّت)";

  // 🛡️ iOS Safari ghost-click guard: when a user taps the "+ Add story"
  // button, the mount + ghost-click sometimes fires the outer backdrop
  // onClose immediately, dismissing the dialog before the user can
  // interact with it. We record the mount time and swallow any backdrop
  // click that happens within the first 400ms.
  const mountedAtRef = useRef(0);
  useEffect(() => {
    mountedAtRef.current = Date.now();
  }, []);
  const handleBackdropClick = (e) => {
    if (Date.now() - mountedAtRef.current < 400) {
      e.stopPropagation();
      return;
    }
    onClose && onClose();
  };

  useEffect(() => {
    if (user?.role === "shop_owner" || user?.role === "admin") {
      api.get("/shops/mine").then(r => {
        const shops = r.data || [];
        setMyShops(shops);
        if (shops.length === 1) setShopId(shops[0].id);
        // If admin/owner has NO shops but can post as journalist/admin story,
        // switch to journalist mode automatically to avoid the confusing "no shop" hint.
        if (shops.length === 0 && canJournalist) {
          setMode("journalist");
        }
      }).catch(() => {
        if (canJournalist) setMode("journalist");
      });
    }
    if (!(user?.role === "shop_owner" || user?.role === "admin") && canJournalist) {
      setMode("journalist");
    }
  }, [user, canJournalist]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) {
      toast.error("يجب أن يكون الملف صورة أو فيديو");
      return;
    }
    // Very high raw caps — client-side compression brings size under the ingress limit
    if (isImg && f.size > 60_000_000) {  // 60 MB raw image
      toast.error("حجم الصورة كبير جداً — أعد المحاولة بصورة أخرى");
      return;
    }
    if (isVid && f.size > 500_000_000) { // 500 MB raw video
      toast.error("حجم الفيديو كبير جداً — استخدم فيديو أقصر أو بجودة أقل");
      return;
    }
    setMediaType(isVid ? "video" : "image");
    const bigVideo = isVid && f.size > 8_000_000;
    const bigImage = isImg && f.size > 500_000;
    if (bigVideo || bigImage) {
      toast.info(bigVideo ? "🎥 جاري ضغط الفيديو للرفع السريع..." : "🖼️ جاري تحسين الصورة...");
    }
    try {
      const { compressImage, compressVideo, fileToDataURL } = await import("@/lib/media-compress");
      const dataUrl = isVid ? await compressVideo(f) : await compressImage(f);
      setMedia(dataUrl);
    } catch {
      const r = new FileReader();
      r.onload = () => setMedia(r.result);
      r.readAsDataURL(f);
    }
  };

  const submit = async () => {
    if (mode === "shop" && !shopId) { toast.error("اختر المحل"); return; }
    if (!media) { toast.error("ارفع صورة أو فيديو"); return; }
    setBusy(true);
    toast.info("⏳ جاري نشر ستوريك...");
    try {
      const payload = {
        shop_id: mode === "shop" ? shopId : "",
        image: media,        // legacy field (backend accepts either)
        media_url: media,
        media_type: mediaType,
        caption,
      };
      await api.post("/stories", payload);
      toast.success("✅ تم نشر الستوري");
      // Close dialog + refresh story bar ONLY after successful upload
      onCreated();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 413 || String(e?.message || "").includes("Network")) {
        toast.error("⚠️ حجم الملف كبير جداً — جرّب صورة/فيديو أصغر", { duration: 5000 });
      } else if (status === 400) {
        toast.error(formatApiErrorDetail(e) || "بيانات الستوري غير صالحة");
      } else if (status === 401 || status === 403) {
        toast.error("غير مصرح — سجّل دخولك مجدداً");
      } else {
        toast.error(formatApiErrorDetail(e) || "فشل النشر، حاول مجدداً");
      }
    } finally {
      setBusy(false);
    }
  };

  const showModeSwitch = canJournalist && myShops.length > 0;

  // ⚡ Instagram-style fullscreen preview when media is already selected.
  // Rendered via a React portal (document.body) so it always covers the
  // entire viewport — bypasses any `transform`/`filter`/`overflow-hidden`
  // ancestor on iOS Safari that would otherwise pin `position: fixed`
  // to the wrong container.
  if (media && !(mode === "shop" && myShops.length === 0 && !canJournalist)) {
    return createPortal((
      <div className="fixed inset-0 z-[999] bg-black flex flex-col" data-testid="add-story-dialog">
        {/* Media fills the entire screen */}
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {mediaType === "image" ? (
            <img src={media} alt="preview" className="w-full h-full object-contain" draggable={false} />
          ) : (
            <video src={media} autoPlay playsInline loop muted={false} className="w-full h-full object-contain" data-testid="story-video-preview" />
          )}
          {/* Text overlay preview */}
          {caption && (
            <div className="absolute inset-x-4 bottom-24 text-center pointer-events-none">
              <div className="inline-block max-w-full px-4 py-2 rounded-xl bg-black/50 backdrop-blur-sm text-white font-bold text-lg break-words whitespace-pre-wrap drop-shadow-2xl">
                {caption}
              </div>
            </div>
          )}
        </div>

        {/* Top bar: Close (X) + Text button (Aa) */}
        <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-3" style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }}>
          <button
            onClick={onClose}
            className="size-10 rounded-full bg-black/40 backdrop-blur-md hover:bg-black/60 flex items-center justify-center text-white transition"
            aria-label="إغلاق"
            data-testid="story-close"
          >
            <X className="size-5" />
          </button>
          <button
            onClick={() => {
              const t = window.prompt("اكتب نصاً على القصة (اختياري):", caption);
              if (t !== null) setCaption(t.slice(0, 200));
            }}
            className={`h-10 px-4 rounded-full backdrop-blur-md flex items-center gap-1.5 text-white transition font-black text-sm ${
              caption ? "bg-primary/80 hover:bg-primary" : "bg-black/40 hover:bg-black/60"
            }`}
            data-testid="story-text-button"
          >
            <span style={{ fontFamily: "serif" }}>Aa</span>
            {caption && <span className="text-xs">✓</span>}
          </button>
        </div>

        {/* Shop selector (only if mode=shop and multiple shops) */}
        {mode === "shop" && myShops.length > 1 && (
          <div className="absolute top-16 inset-x-3 z-10" style={{ marginTop: "env(safe-area-inset-top)" }}>
            <select
              value={shopId}
              onChange={e => setShopId(e.target.value)}
              className="w-full h-11 rounded-full bg-black/50 backdrop-blur-md px-4 text-sm text-white border border-white/20 text-center font-bold"
              data-testid="story-shop-select"
            >
              <option value="" className="bg-black">اختر المحل...</option>
              {myShops.map(s => <option key={s.id} value={s.id} className="bg-black">{s.name}</option>)}
            </select>
          </div>
        )}

        {/* Bottom "Publish story" button */}
        <div className="absolute bottom-0 inset-x-0 z-10 p-4 flex items-center gap-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
          {/* Small circular thumbnail preview */}
          <div className="size-12 rounded-full border-2 border-white overflow-hidden shrink-0 bg-black">
            {mediaType === "image" ? (
              <img src={media} alt="" className="w-full h-full object-cover" />
            ) : (
              <video src={media} className="w-full h-full object-cover" muted />
            )}
          </div>
          <button
            onClick={submit}
            disabled={busy || (mode === "shop" && !shopId)}
            className="flex-1 h-12 rounded-full bg-white text-black font-black gap-2 flex items-center justify-center disabled:opacity-60 shadow-2xl transition hover:bg-white/90 active:scale-95"
            data-testid="story-submit"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            <span>نشر قصتك</span>
          </button>
        </div>
      </div>
    ), document.body);
  }

  // 📱 Initial fullscreen "pick a file" screen (Instagram-style, no small popup).
  // Rendered via portal so it always fills the viewport, never hides
  // below scroll or gets clipped by any ancestor `transform`.
  return createPortal((
    <div
      className="fixed inset-0 z-[999] bg-black flex flex-col"
      data-testid="add-story-dialog"
      onClick={handleBackdropClick}
    >
      {/* Ambient gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-black to-slate-900" />

      {/* Top bar with close button */}
      <div
        className="relative z-10 flex items-center justify-between px-4 py-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }}
      >
        <button
          onClick={onClose}
          className="size-10 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 flex items-center justify-center text-white transition"
          aria-label="إغلاق"
          data-testid="story-close-initial"
        >
          <X className="size-5" />
        </button>
        <h3 className="text-white font-black text-lg">ستوري جديد</h3>
        <div className="size-10" />{/* spacer for symmetry */}
      </div>

      {/* Content area (centered) */}
      <div
        className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 gap-6 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mode switcher (only when user is admin/journalist AND owns shops) */}
        {showModeSwitch && (
          <div className="w-full max-w-md grid grid-cols-2 gap-2 p-1 bg-white/10 backdrop-blur-md rounded-2xl">
            <button
              type="button"
              onClick={() => setMode("shop")}
              className={`py-3 text-sm font-black rounded-xl transition ${
                mode === "shop" ? "bg-white text-black shadow-lg" : "text-white/70"
              }`}
              data-testid="story-mode-shop"
            >
              ستوري محل
            </button>
            <button
              type="button"
              onClick={() => setMode("journalist")}
              className={`py-3 text-sm font-black rounded-xl transition ${
                mode === "journalist"
                  ? (isAdmin ? "bg-amber-500 text-white shadow-lg" : "bg-blue-500 text-white shadow-lg")
                  : "text-white/70"
              }`}
              data-testid="story-mode-journalist"
            >
              {journalistLabel}
            </button>
          </div>
        )}

        {mode === "journalist" && (
          <div className="w-full max-w-md text-xs text-blue-100 bg-blue-500/15 border border-blue-400/30 rounded-xl p-3 flex items-start gap-2">
            <span className="text-lg">📰</span>
            <div>
              <div className="font-bold mb-0.5">ستوري مراسل</div>
              <div>سيظهر <b>مثبّتاً في مقدمة</b> شريط الستوريات ولا يرتبط بمحل.</div>
            </div>
          </div>
        )}

        {mode === "shop" && myShops.length === 0 && !canJournalist ? (
          <div className="w-full max-w-md text-sm text-white/80 bg-white/10 rounded-2xl p-6 text-center">
            لا يوجد محل تملكه. أضف محلاً أولاً.
          </div>
        ) : (
          <>
            {/* Big tap area to pick a file */}
            <label
              htmlFor="story-file-input"
              className="w-full max-w-md aspect-[3/4] rounded-3xl bg-white/5 border-2 border-dashed border-white/25 hover:border-primary/70 hover:bg-white/10 flex flex-col items-center justify-center gap-4 cursor-pointer transition active:scale-[0.98]"
              data-testid="story-picker-tap-area"
            >
              <div className="size-20 rounded-full bg-primary/20 flex items-center justify-center">
                <Upload className="size-10 text-primary" />
              </div>
              <div className="text-center">
                <div className="text-white text-lg font-black mb-1">اختر صورة أو فيديو</div>
                <div className="text-white/60 text-xs">اضغط هنا لاختيار من ألبومك</div>
              </div>
            </label>

            {/* Hidden native input triggered by the label above */}
            <input
              id="story-file-input"
              type="file"
              accept="image/*,video/*"
              onChange={onFile}
              className="hidden"
              data-testid="story-file-input"
            />

            <div className="text-[11px] text-center text-white/50">
              بعد الاختيار ستنتقل لشاشة النشر مباشرة
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
